import type { ReactNode } from "react";
import { SlotLabelsMultiCombobox } from "./SlotLabelCombobox";
import type { MfgRecipeSummary } from "@/lib/manufacturingClient";
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass, mfgInlineInputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { ManufacturingModelsAttachmentSectionModel } from "./useManufacturingModelsAttachmentSection";

type Props = {
  selectedId: string | null;
  a: ManufacturingModelsAttachmentSectionModel;
  orgSlotLabels?: readonly string[];
};

export function ManufacturingModelsAttachmentsEditor({ selectedId, a, orgSlotLabels = [] }: Props) {
  if (!selectedId) return null;

  return (
    <SectionCard
      title="Attachments (packages / option swaps)"
      description={
        <>
          Attach packages or options onto this model. Use <code>replaces_slot_labels</code> to
          remove parent lines tagged with those slots (comma-separated). For material deltas and
          spacing upgrades, use the <strong>Variants</strong> section below.
        </>
      }
    >
      <AttachmentRowsEditor a={a} orgSlotLabels={orgSlotLabels} />
    </SectionCard>
  );
}

export function AttachmentRowsEditor({
  a,
  orgSlotLabels = [],
  showDefaultIn = true,
  showPriceDelta = true,
  readOnly = false,
  previewSlot,
}: {
  a: ManufacturingModelsAttachmentSectionModel;
  orgSlotLabels?: readonly string[];
  showDefaultIn?: boolean;
  showPriceDelta?: boolean;
  readOnly?: boolean;
  previewSlot?: (rowKey: string) => ReactNode;
}) {
  return (
    <>
      {a.loadBusy ? <div className="text-xs text-gray-500 dark:text-zinc-400">Loading…</div> : null}

      <div className="space-y-2">
        {!a.loadBusy && a.rows.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-zinc-400">None yet.</div>
        ) : null}
        {a.rows.map((row) => (
          <div
            key={row.key}
            className="flex flex-wrap items-center gap-2 border-b border-gray-100 py-2 last:border-b-0 dark:border-zinc-700"
          >
            <select
              className={`${mfgInlineInputClass} min-w-[180px] flex-1`}
              value={row.attached_recipe_id}
              onChange={(e) => a.patchRow(row.key, { attached_recipe_id: e.target.value })}
              disabled={readOnly}
            >
              <option value="">Select recipe…</option>
              {a.candidateRecipes.map((r: MfgRecipeSummary) => (
                <option key={r.id} value={r.id}>
                  [{r.kind ?? "assembly"}] {r.code} — {r.name}
                </option>
              ))}
            </select>

            <input
              className={`${mfgInlineInputClass} max-w-[90px]`}
              inputMode="decimal"
              placeholder="Qty"
              value={row.quantity}
              onChange={(e) => a.patchRow(row.key, { quantity: e.target.value })}
              disabled={readOnly}
            />

            {showDefaultIn ? (
              <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-zinc-300">
                <input
                  type="checkbox"
                  checked={row.included_by_default}
                  onChange={(e) =>
                    a.patchRow(row.key, { included_by_default: e.target.checked })
                  }
                  disabled={readOnly}
                />
                Default-in
              </label>
            ) : null}

            {showPriceDelta ? (
              <input
                className={`${mfgInlineInputClass} max-w-[110px]`}
                inputMode="decimal"
                placeholder="$ price delta"
                value={row.customer_price_delta}
                onChange={(e) => a.patchRow(row.key, { customer_price_delta: e.target.value })}
                disabled={readOnly}
              />
            ) : null}

            <SlotLabelsMultiCombobox
              value={row.replaces_slot_labels}
              suggestions={orgSlotLabels}
              disabled={readOnly}
              onChange={(v) => a.patchRow(row.key, { replaces_slot_labels: v })}
            />

            <input
              className={`${mfgInlineInputClass} min-w-[140px]`}
              placeholder="Notes"
              value={row.notes}
              onChange={(e) => a.patchRow(row.key, { notes: e.target.value })}
              disabled={readOnly}
            />

            {previewSlot ? previewSlot(row.key) : null}

            {!readOnly ? (
              <button type="button" className={ghostBtnClass} onClick={() => a.removeRow(row.key)}>
                Remove
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {!readOnly ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className={ghostBtnClass}
            onClick={a.addRow}
            disabled={!a.defaultAttachedRecipeId}
          >
            + Row
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            onClick={() => void a.onSave()}
            disabled={a.saveBusy}
          >
            {a.saveBusy ? "Saving…" : "Save"}
          </button>
        </div>
      ) : null}
    </>
  );
}
